import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import AppBreadcrumb from "../Shared/AppBreadcrumb";
import queryString from "query-string";
import useAlert from "../../hooks/useAlert";
import httpService from "../../services/http.service";
import FecthingSpinner from "../Shared/FecthingSpinner";
import { Badge, Col, Form, FormGroup, Label } from "reactstrap";
import AppAlert from "../Shared/AppAlert";
import AppInput from "../Shared/AppInput";
import { useFormik } from "formik";
import * as Yup from "yup";
import { extractResponseValidationErrors } from "../../utils";
import { serialize } from "object-to-formdata";
import AppCheckbox from "../Shared/AppCheckbox";
import ReferralBudgetPanel from "./ReferralBudgetPanel";

const Setting = ({ history, location, match, ...props }) => {
    const { t } = useTranslation();

    const initialValues = {
        current_ios_version: '',
        current_android_version: '',
        current_huawei_version: '',
        setting_fallback_base_api: '',
        setting_google_translate_api_key: '',
        setting_google_translate_api_key_clear: false,
        setting_header_logo: '',
        setting_opinions_pagination: '',
        setting_comments_pagination: '',
        setting_youtube_link: '',
        setting_for_you_api_enabled: false,
        setting_cashout_enabled: false,
        setting_coin_exchange_usd: '',
        setting_like_exchange_usd: '',
        setting_view_exchange_usd: '',
        setting_cashout_min_usd: '',
        setting_cashout_fees_xof: '',
        setting_exchange_fees: '',
        setting_top_comment_reward: '',
        setting_top_suggestion_reward: '',
        setting_special_content_min_followers: '',
        setting_special_content_creator_percentage: '',
        setting_referral_enabled: false,
        setting_referral_signup_bonus_usd: '',
        setting_referral_referrer_bonus_usd: '',
        setting_referral_max_per_referrer: '',
        // Champ d'interface (non envoyé) : « Illimité » = plafond null.
        setting_referral_max_unlimited: false,
        setting_referral_budget_usd: '',
        setting_referral_budget_period: 'month',
        setting_referral_budget_mode: 'reserve',
        setting_referral_pending_expiry_days: '',
        setting_referral_alert_emails: '',
        setting_referral_alert_users: '',
        setting_referral_require_referrer_kyc: false,
        setting_cashout_require_kyc: false,
        setting_wallet_require_kyc: false,
        setting_referral_block_disposable_email: true,
    };

    // Budget, alertes, KYC : disponibles seulement après la 2e migration du parrainage (apps/api).
    const budgetKeys = [
        'setting_referral_budget_usd',
        'setting_referral_budget_period',
        'setting_referral_budget_mode',
        'setting_referral_pending_expiry_days',
        'setting_referral_alert_emails',
        'setting_referral_alert_users',
        'setting_referral_require_referrer_kyc',
        'setting_cashout_require_kyc',
        'setting_wallet_require_kyc',
        'setting_referral_block_disposable_email',
    ];

    const periodLabels = { day: t('Par jour'), month: t('Par mois'), total: t('Total') };
    const modeLabels = { reserve: t("Réservation à l'inscription"), no_reserve: t('Sans réservation') };

    const referralKeys = [
        'setting_referral_enabled',
        'setting_referral_signup_bonus_usd',
        'setting_referral_referrer_bonus_usd',
        'setting_referral_max_per_referrer',
    ];

    // Montant en USD entre 0 et 100 (même garde-fou que l'API).
    const usdAmount = () => Yup.string()
        .required(t("Champ obligatoire"))
        .test('usd', t('Montant invalide (entre 0 et 100)'), value => {
            const amount = Number(value);
            return value !== '' && !Number.isNaN(amount) && amount >= 0 && amount <= 100;
        });

    const rewards = [
        { value: "COOL", label: "COOL", coins: 1 },
        { value: "BRAVO", label: "BRAVO", coins: 5 },
        { value: "SUPER", label: "SUPER", coins: 10 },
        { value: "GENIAL", label: "GENIAL", coins: 15 },
        { value: "WOW", label: "WOW", coins: 25 },
        { value: "LOVE", label: "LOVE", coins: 50 },
        { value: "IMPRESSIVE", label: "IMPRESSIVE", coins: 75 },
        { value: "OMG", label: "OMG", coins: 100 },
        { value: "SILVER", label: "SILVER", coins: 250 },
        { value: "GOLD", label: "GOLD", coins: 500 },
        { value: "DIAMOND", label: "DIAMOND", coins: 1000 },
        { value: "ROYALTY", label: "ROYALTY", coins: 2000 },
    ];

    const query = queryString.parse(location.search);

    const [alert, setAlert, onClose] = useAlert();
    const [submitting, setSubmitting] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [setting, setSetting] = useState({});
    const [activeSection, setActiveSection] = useState(null);
    const [file, setFile] = useState(null);

    const onSubmit = async (values) => {

        setSubmitting(true);
        setAlert(null);

        const payload = { ...values };
        if (!setting.setting_referral_available) {
            referralKeys.forEach(key => delete payload[key]);
        }
        if (!setting.setting_referral_budget_available) {
            budgetKeys.forEach(key => delete payload[key]);
        }
        if (payload.setting_referral_max_unlimited) {
            payload.setting_referral_max_per_referrer = '';
        }
        delete payload.setting_referral_max_unlimited;

        const formData = serialize(payload, {
            indices: true,
            booleansAsIntegers: true
        });

        try {

            const { data: response } = await httpService.post(`/app_settings`, formData);
            setSetting(state => ({ ...state, ...response?.data }));
            fetchSetting({}, () => {
                setFetching(false);
            });
            onCancelSettingEdit();

        }
        catch ({ response }) {
            const { data, status } = response;

            if (response && status === 422) {
                setAlert({
                    type: 'danger',
                    message: extractResponseValidationErrors(data)[0]
                })
            }
            else if (response && status === 500) {
                setAlert({
                    type: 'danger',
                    message: t('Erreur de traitement, veuillez contacter les administrateurs')
                })
            }
        }
        finally {
            setSubmitting(false);
        }

    };

    const onFileSelect = (event) => {
        const { target } = event;
        const files = target.files;

        setFile(files[0]);

        formik.setFieldValue('setting_header_logo', files[0], true);
        formik.setFieldTouched('setting_header_logo', false);
    };

    const onSettingEdit = (sectionKey) => {
        formik.setValues({
            ...initialValues,
            current_ios_version: setting.current_ios_version || '',
            current_android_version: setting.current_android_version || '',
            current_huawei_version: setting.current_huawei_version || '',
            setting_fallback_base_api: setting.setting_fallback_base_api || '',
            // La clé n'est jamais préremplie : vide = inchangée, une nouvelle valeur la remplace.
            setting_google_translate_api_key: '',
            setting_google_translate_api_key_clear: false,
            setting_header_logo: setting.setting_header_logo || '',
            setting_opinions_pagination: setting.setting_opinions_pagination || '',
            setting_comments_pagination: setting.setting_comments_pagination || '',
            setting_youtube_link: setting.setting_youtube_link || '',
            setting_for_you_api_enabled: setting.setting_for_you_api_enabled || false,
            setting_cashout_enabled: setting.setting_cashout_enabled || false,
            setting_coin_exchange_usd: setting.setting_coin_exchange_usd || '',
            setting_like_exchange_usd: setting.setting_like_exchange_usd || '',
            setting_view_exchange_usd: setting.setting_view_exchange_usd || '',
            setting_cashout_min_usd: setting.setting_cashout_min_usd || '',
            setting_cashout_fees_xof: setting.setting_cashout_fees_xof || '',
            setting_exchange_fees: setting.setting_exchange_fees || '',
            setting_top_comment_reward: setting.setting_top_comment_reward || '',
            setting_top_suggestion_reward: setting.setting_top_suggestion_reward || '',
            setting_special_content_min_followers: setting.setting_special_content_min_followers || '',
            setting_special_content_creator_percentage: setting.setting_special_content_creator_percentage || '',
            setting_referral_enabled: setting.setting_referral_enabled || false,
            setting_referral_signup_bonus_usd: setting.setting_referral_signup_bonus_usd ?? '',
            setting_referral_referrer_bonus_usd: setting.setting_referral_referrer_bonus_usd ?? '',
            setting_referral_max_per_referrer: setting.setting_referral_max_per_referrer ?? '',
            setting_referral_max_unlimited: setting.setting_referral_max_per_referrer == null,
            setting_referral_budget_usd: setting.setting_referral_budget_usd ?? '',
            setting_referral_budget_period: setting.setting_referral_budget_period || 'month',
            setting_referral_budget_mode: setting.setting_referral_budget_mode || 'reserve',
            setting_referral_pending_expiry_days: setting.setting_referral_pending_expiry_days ?? '',
            setting_referral_alert_emails: setting.setting_referral_alert_emails || '',
            setting_referral_alert_users: setting.setting_referral_alert_users || '',
            setting_referral_require_referrer_kyc: !!setting.setting_referral_require_referrer_kyc,
            setting_cashout_require_kyc: !!setting.setting_cashout_require_kyc,
            setting_wallet_require_kyc: !!setting.setting_wallet_require_kyc,
            setting_referral_block_disposable_email: setting.setting_referral_block_disposable_email !== false,
        });
        setActiveSection(sectionKey);
    }

    const onCancelSettingEdit = () => {
        formik.resetForm({ ...initialValues });
        setAlert(null);
        setActiveSection(null);
    };

    const fetchSetting = async (params = {}, cb = () => null) => {
        const { data: response } = await httpService.get(`/app_settings`, {
            params
        });
        console.log(response)
        setSetting(response.data);
        cb();
    };

    useEffect(() => {

        try {

            fetchSetting({}, () => {
                setFetching(false);
            });

        } catch (e) {
            throw e;
        }

    }, []);


    // ---------------------------------------------------------------------------------------------------
    // Catégories : chacune a son tableau de valeurs et SA fenêtre de modification (jamais regroupées).
    // `fields` = champs modifiables (type : input | checkbox | select | reward | referral-max) ;
    // `validates` = champs supplémentaires à valider avec ce champ.
    // ---------------------------------------------------------------------------------------------------
    const yesNo = (value) => value
        ? <Badge color={"success"}>{t("Oui")}</Badge>
        : <Badge color={"danger"}>{t("Non")}</Badge>;
    const unavailable = <Badge color={"warning"}>{t("Indisponible : mise à jour de la base de données en attente")}</Badge>;
    const rate = setting['setting_exchange_rate_usd_to_xof'];

    const sectionsConfig = [
        {
            key: 'mobile',
            icon: 'fa-mobile',
            title: t("Application mobile"),
            description: t("Versions publiées sur les stores."),
            rows: [
                { label: t("Version iOS"), value: setting['current_ios_version'] || '-' },
                { label: t("Version Android"), value: setting['current_android_version'] || '-' },
                { label: t("Version Huawei"), value: setting['current_huawei_version'] || '-' },
            ],
            fields: [
                { type: 'input', name: 'current_ios_version', label: t("Version iOS"), required: true, md: 4 },
                { type: 'input', name: 'current_android_version', label: t("Version Android"), required: true, md: 4 },
                { type: 'input', name: 'current_huawei_version', label: t("Version Huawei"), required: true, md: 4 },
            ],
        },
        {
            key: 'content',
            icon: 'fa-newspaper-o',
            title: t("Contenu et affichage"),
            description: t("Pagination, fil « For You » et identité visuelle."),
            rows: [
                { label: t("Pagination des opinions"), value: setting['setting_opinions_pagination'] || '-' },
                { label: t("Pagination des commentaires"), value: setting['setting_comments_pagination'] || '-' },
                { label: t("For You"), value: yesNo(setting['setting_for_you_api_enabled']) },
                ...((process.env.REACT_APP_SOCIAL_HUB && process.env.REACT_APP_USE_SETTING_YOUTUBE_LINK)
                    ? [{ label: t("Lien média"), value: setting['setting_youtube_link'] || '-' }] : []),
                { label: t("Header logo"), value: setting['setting_header_logo_url']
                    ? <img className="img-fluid" style={{ maxHeight: 48 }} src={setting['setting_header_logo_url']} alt="Logo" /> : '-' },
            ],
            fields: [
                { type: 'input', name: 'setting_opinions_pagination', label: t("Pagination des opinions"), required: true, md: 6 },
                { type: 'input', name: 'setting_comments_pagination', label: t("Pagination des commentaires"), required: true, md: 6 },
                { type: 'input', name: 'setting_header_logo', label: t("Fichier Header Logo"), required: true },
                ...((process.env.REACT_APP_SOCIAL_HUB && process.env.REACT_APP_USE_SETTING_YOUTUBE_LINK)
                    ? [{ type: 'input', name: 'setting_youtube_link', label: t("Lien youtube") }] : []),
                { type: 'checkbox', name: 'setting_for_you_api_enabled', label: t("Activer 'For-You' ?") },
            ],
        },
        {
            key: 'services',
            icon: 'fa-plug',
            title: t("Services externes"),
            description: t("API de secours et traduction automatique."),
            rows: [
                { label: t("Base API"), value: setting['setting_fallback_base_api'] || '-' },
                { label: t("Google Translate API Key"), value: setting['setting_google_translate_api_key'] ? <code>{setting['setting_google_translate_api_key']}</code> : <Badge color={"warning"}>{t("Non configurée")}</Badge> },
            ],
            fields: [
                { type: 'input', name: 'setting_fallback_base_api', label: t("Fallback Base API"), inputType: 'url', placeholder: 'https://domain.com' },
                { type: 'input', name: 'setting_google_translate_api_key', label: t("Google Translate API Key"), inputType: 'password', autoComplete: 'new-password',
                    placeholder: setting['setting_google_translate_api_key_set'] ? t("Laisser vide pour conserver la clé actuelle") : t("Coller la clé API") },
                ...(setting['setting_google_translate_api_key_set'] ? [{ type: 'checkbox', name: 'setting_google_translate_api_key_clear', label: t("Supprimer la clé enregistrée") }] : []),
            ],
        },
        {
            key: 'monetization',
            icon: 'fa-money',
            title: t("Monétisation"),
            description: t("Taux de conversion, retraits et frais."),
            rows: [
                { label: t("Service Cashout"), value: yesNo(setting['setting_cashout_enabled']) },
                { label: t("Coin Exchange (USD)"), value: `${setting['setting_coin_exchange_usd'] || '-'} USD` },
                { label: t("Like Exchange (USD)"), value: `${setting['setting_like_exchange_usd'] || '-'} USD` },
                { label: t("View Exchange (USD)"), value: `${setting['setting_view_exchange_usd'] || '-'} USD` },
                { label: t("Cashout Minimum (USD)"), value: `${setting['setting_cashout_min_usd'] || '-'} USD (${(setting['setting_cashout_min_usd'] * rate)?.toFixed(0)} XOF)` },
                { label: t("Cashout Fees (XOF)"), value: `${setting['setting_cashout_fees_xof'] || '-'} XOF (${(setting['setting_cashout_fees_xof'] / (rate + ((rate * setting['setting_exchange_fees']) / 100)))?.toFixed(2)} USD)` },
                { label: t("Exchange Rate USD to XOF"), value: `${rate || '-'} XOF` },
                { label: t("Exchange Fees (%)"), value: `${setting['setting_exchange_fees'] || '-'}%` },
            ],
            fields: [
                { type: 'checkbox', name: 'setting_cashout_enabled', label: t("Activer le service cashout ?") },
                { type: 'input', name: 'setting_coin_exchange_usd', label: t("Coin Exchange (USD)"), inputType: 'number', step: '0.00001' },
                { type: 'input', name: 'setting_like_exchange_usd', label: t("Like Exchange (USD)"), inputType: 'number', step: '0.00001' },
                { type: 'input', name: 'setting_view_exchange_usd', label: t("View Exchange (USD)"), inputType: 'number', step: '0.00001' },
                { type: 'input', name: 'setting_cashout_min_usd', label: t("Cashout Minimum (USD)"), inputType: 'number', step: '0.00001' },
                { type: 'input', name: 'setting_cashout_fees_xof', label: t("Cashout Fees (XOF)"), inputType: 'number', step: '0.00001' },
                { type: 'input', name: 'setting_exchange_fees', label: t("Exchange Fees (%)"), inputType: 'number', step: '0.00001' },
            ],
        },
        {
            key: 'creators',
            icon: 'fa-star',
            title: t("Contenu spécial"),
            description: t("Conditions pour créer du contenu spécial."),
            rows: [
                { label: t("Minimum Followers (Contenu Spécial)"), value: setting['setting_special_content_min_followers'] || '-' },
                { label: t("Pourcentage Créateur (Contenu Spécial)"), value: `${setting['setting_special_content_creator_percentage'] || '-'}%` },
            ],
            fields: [
                { type: 'input', name: 'setting_special_content_min_followers', label: t("Minimum Followers (Contenu Spécial)"), inputType: 'number', step: '1' },
                { type: 'input', name: 'setting_special_content_creator_percentage', label: t("Pourcentage Créateur (Contenu Spécial)"), inputType: 'number', step: '0.01' },
            ],
        },
        {
            key: 'rewards',
            icon: 'fa-trophy',
            title: t("Récompenses"),
            description: t("Récompenses des meilleurs commentaires et suggestions."),
            rows: [
                { label: t("Récompense Top Comment"), value: setting['setting_top_comment_reward'] || '-' },
                { label: t("Récompense Top Suggestion"), value: setting['setting_top_suggestion_reward'] || '-' },
            ],
            fields: [
                { type: 'reward', name: 'setting_top_comment_reward', label: t("Récompense Top Comment"), required: true },
                { type: 'reward', name: 'setting_top_suggestion_reward', label: t("Récompense Top Suggestion"), required: true },
            ],
        },
        {
            key: 'referral',
            icon: 'fa-users',
            title: t("Parrainage"),
            description: t("Bonus, plafond, budget et alertes."),
            unavailable: !setting['setting_referral_available'],
            rows: setting['setting_referral_available'] ? [
                { label: t("Parrainage activé"), value: yesNo(setting['setting_referral_enabled']) },
                { label: t("Bonus d'inscription du filleul"), value: `${setting['setting_referral_signup_bonus_usd'] ?? '-'} USD` },
                { label: t("Bonus du parrain"), value: `${setting['setting_referral_referrer_bonus_usd'] ?? '-'} USD` },
                { label: t("Plafond de filleuls récompensés par parrain"), value: setting['setting_referral_max_per_referrer'] ?? t('Illimité') },
                ...(setting['setting_referral_budget_available'] ? [
                    {
                        label: t("Budget de parrainage"),
                        value: (
                            <>
                                {Number(setting['setting_referral_budget_usd'] || 0) > 0
                                    ? `${setting['setting_referral_budget_usd']} USD · ${periodLabels[setting['setting_referral_budget_period']] || ''} · ${modeLabels[setting['setting_referral_budget_mode']] || ''}`
                                    : <Badge color={"warning"}>{t("Non défini : aucun versement tant que le budget est à 0")}</Badge>}
                                <ReferralBudgetPanel
                                    budget={setting['referral_budget']}
                                    announcement={setting['referral_announcement']}
                                    onQueued={() => fetchSetting()}
                                />
                            </>
                        ),
                    },
                    { label: t("Expiration d'un parrainage en attente"), value: `${setting['setting_referral_pending_expiry_days']} ${t('jours')}` },
                    { label: t("E-mails des alertes de parrainage"), value: setting['setting_referral_alert_emails'] || i18n.t("SERVICE_ALERT_EMAILS (par défaut)") },
                    { label: t("Comptes recevant les alertes push"), value: setting['setting_referral_alert_users'] || '-' },
                    { label: t("Bloquer les e-mails jetables (parrainage)"), value: yesNo(setting['setting_referral_block_disposable_email']) },
                ] : [{ label: t("Budget de parrainage"), value: unavailable }]),
            ] : [{ label: t("Parrainage"), value: unavailable }],
            hint: t("Les bonus sont versés après la vérification de l'e-mail du filleul. Les montants sont en USD et convertis en coins au moment du versement."),
            fields: [
                { type: 'checkbox', name: 'setting_referral_enabled', label: t("Activer le parrainage ?") },
                { type: 'input', name: 'setting_referral_signup_bonus_usd', label: t("Bonus d'inscription du filleul (USD)"), inputType: 'number', step: '0.001', min: '0', max: '100', required: true, md: 6 },
                { type: 'input', name: 'setting_referral_referrer_bonus_usd', label: t("Bonus du parrain (USD)"), inputType: 'number', step: '0.001', min: '0', max: '100', required: true, md: 6 },
                { type: 'referral-max', name: 'setting_referral_max_per_referrer', validates: ['setting_referral_max_unlimited'] },
                ...(setting['setting_referral_budget_available'] ? [
                    { type: 'input', name: 'setting_referral_budget_usd', label: t("Budget de parrainage (USD, 0 = aucun versement)"), inputType: 'number', step: '0.01', min: '0', required: true },
                    { type: 'select', name: 'setting_referral_budget_period', label: t("Période du budget"), options: [['day', t("Par jour")], ['month', t("Par mois")], ['total', t("Total (sans renouvellement)")]], md: 6 },
                    { type: 'select', name: 'setting_referral_budget_mode', label: t("Mode du budget"), options: [['reserve', t("Réservation : le coût est réservé à l'inscription")], ['no_reserve', t("Sans réservation : bonus perdu si le budget est épuisé à la vérification")]], md: 6 },
                    { type: 'input', name: 'setting_referral_pending_expiry_days', label: t("Expiration d'un parrainage en attente (jours, mode réservation)"), inputType: 'number', step: '1', min: '1', max: '365', required: true },
                    { type: 'input', name: 'setting_referral_alert_emails', label: t("E-mails des alertes de parrainage (séparés par des virgules ; vide = SERVICE_ALERT_EMAILS)") },
                    { type: 'input', name: 'setting_referral_alert_users', label: t("Comptes recevant les alertes push (user_id ou username, séparés par des virgules)") },
                    { type: 'checkbox', name: 'setting_referral_block_disposable_email', label: t("Pas de bonus pour les e-mails jetables (l'inscription reste possible)") },
                ] : []),
            ],
        },
        {
            key: 'kyc',
            icon: 'fa-id-card-o',
            title: t("Vérification d'identité (KYC)"),
            description: t("Opérations qui exigent un KYC validé."),
            unavailable: !setting['setting_referral_budget_available'],
            rows: setting['setting_referral_budget_available'] ? [
                { label: t("KYC du parrain pour obtenir un code"), value: yesNo(setting['setting_referral_require_referrer_kyc']) },
                { label: t("KYC pour les retraits"), value: yesNo(setting['setting_cashout_require_kyc']) },
                { label: t("KYC pour ajouter / modifier un portefeuille"), value: yesNo(setting['setting_wallet_require_kyc']) },
            ] : [{ label: t("KYC"), value: unavailable }],
            fields: [
                { type: 'checkbox', name: 'setting_referral_require_referrer_kyc', label: t("Exiger le KYC du parrain pour obtenir un code de parrainage") },
                { type: 'checkbox', name: 'setting_cashout_require_kyc', label: t("Exiger le KYC pour les retraits") },
                { type: 'checkbox', name: 'setting_wallet_require_kyc', label: t("Exiger le KYC pour ajouter / modifier un portefeuille de retrait") },
            ],
        },
    ];
    const activeSectionRef = useRef(null);
    activeSectionRef.current = activeSection;

    const fullShape = {
            current_ios_version: Yup.string().required(t("Champ obligatoire")),
            current_android_version: Yup.string().required(t("Champ obligatoire")),
            current_huawei_version: Yup.string().required(t("Champ obligatoire")),
            setting_opinions_pagination: Yup.string().required(t("Champ obligatoire")),
            setting_comments_pagination: Yup.string().required(t("Champ obligatoire")),
            setting_fallback_base_api: Yup.string().nullable(),
            setting_youtube_link: Yup.string().nullable(),
            setting_google_translate_api_key: Yup.string().nullable(),
            setting_header_logo: Yup.string().nullable(),
            setting_coin_exchange_usd: Yup.string().nullable(),
            setting_like_exchange_usd: Yup.string().nullable(),
            setting_view_exchange_usd: Yup.string().nullable(),
            setting_cashout_min_usd: Yup.string().nullable(),
            setting_cashout_fees_xof: Yup.string().nullable(),
            setting_exchange_fees: Yup.string().nullable(),
            setting_top_comment_reward: Yup.string().required(t("Champ obligatoire")),
            setting_top_suggestion_reward: Yup.string().required(t("Champ obligatoire")),
            setting_special_content_min_followers: Yup.string().nullable(),
            setting_special_content_creator_percentage: Yup.string().nullable(),
            ...(setting.setting_referral_available ? {
                setting_referral_signup_bonus_usd: usdAmount(),
                setting_referral_referrer_bonus_usd: usdAmount(),
                // Nombre >= 1, ou « Illimité » coché.
                setting_referral_max_per_referrer: Yup.string().nullable().test(
                    'max',
                    t('Entier supérieur ou égal à 1, ou cochez « Illimité »'),
                    function (value) {
                        if (this.parent.setting_referral_max_unlimited) {
                            return true;
                        }
                        return /^\d+$/.test(String(value ?? '')) && Number(value) >= 1;
                    }
                ),
            } : {}),
            ...(setting.setting_referral_budget_available ? {
                setting_referral_budget_usd: Yup.string().required(t("Champ obligatoire")).test(
                    'budget', t('Montant invalide (0 = aucun versement)'),
                    value => value !== '' && !Number.isNaN(Number(value)) && Number(value) >= 0
                ),
                setting_referral_pending_expiry_days: Yup.string().required(t("Champ obligatoire")).test(
                    'expiry', t('Entier entre 1 et 365'),
                    value => /^\d+$/.test(String(value ?? '')) && Number(value) >= 1 && Number(value) <= 365
                ),
                setting_referral_alert_emails: Yup.string().nullable().test(
                    'emails', t('Adresse e-mail invalide'),
                    value => !value || value.split(/[\s,;]+/).filter(Boolean).every(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
                ),
            } : {}),
        };

    // Chaque catégorie se modifie et se valide seule : seuls les champs de la section ouverte sont contrôlés.
    const shapeFor = (sectionKey) => {
        const section = sectionsConfig.find(item => item.key === sectionKey);
        if (!section) return {};
        return section.fields.reduce((acc, field) => {
            [field.name, ...(field.validates || [])].forEach(name => {
                if (fullShape[name]) acc[name] = fullShape[name];
            });
            return acc;
        }, {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.lazy(() => Yup.object().shape(shapeFor(activeSectionRef.current))),
        onSubmit
    });

    const currentSection = sectionsConfig.find(item => item.key === activeSection);

    const renderField = (field) => {
        const error = formik.errors[field.name];
        const touched = formik.touched[field.name];
        switch (field.type) {
            case 'checkbox':
                return (
                    <FormGroup>
                        <AppCheckbox
                            name={field.name}
                            label={field.label}
                            onChange={(e) => formik.setFieldValue(field.name, e.target.checked)}
                            checked={!!formik.values[field.name]}
                        />
                    </FormGroup>
                );
            case 'select':
                return (
                    <FormGroup>
                        <Label className="control-label" htmlFor={field.name}>{field.label}</Label>
                        <select id={field.name} name={field.name} className="form-control" value={formik.values[field.name]} onChange={formik.handleChange}>
                            {field.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                        </select>
                    </FormGroup>
                );
            case 'reward':
                return (
                    <FormGroup>
                        <div>
                            <Label htmlFor={field.name} className="control-label">{field.label}</Label>
                            {field.required && <span className="text-danger">*</span>}
                        </div>
                        <select id={field.name} name={field.name} className="form-control" required={field.required} value={formik.values[field.name]} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                            <option value="">{t("Sélectionner une récompense")}</option>
                            {rewards.map((reward) => (
                                <option key={reward.value} value={reward.value}>{reward.label} ({reward.coins} coins)</option>
                            ))}
                        </select>
                    </FormGroup>
                );
            case 'referral-max':
                return (
                    <FormGroup>
                        <AppInput
                            label={t("Plafond de filleuls récompensés par parrain")}
                            name="setting_referral_max_per_referrer"
                            error={formik.errors.setting_referral_max_per_referrer}
                            touched={formik.touched.setting_referral_max_per_referrer}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.setting_referral_max_unlimited ? '' : formik.values.setting_referral_max_per_referrer}
                            disabled={formik.values.setting_referral_max_unlimited}
                            type="number"
                            step="1"
                            min="1"
                        />
                        <AppCheckbox
                            name="setting_referral_max_unlimited"
                            label={t("Illimité")}
                            onChange={(e) => formik.setFieldValue('setting_referral_max_unlimited', e.target.checked)}
                            checked={formik.values.setting_referral_max_unlimited}
                        />
                    </FormGroup>
                );
            default:
                return (
                    <FormGroup>
                        <AppInput
                            label={field.label}
                            name={field.name}
                            type={field.inputType}
                            step={field.step}
                            min={field.min}
                            max={field.max}
                            placeholder={field.placeholder}
                            autoComplete={field.autoComplete}
                            error={error}
                            touched={touched}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values[field.name]}
                            required={field.required}
                        />
                    </FormGroup>
                );
        }
    };

    return (
        <>
            <div className="container-fluid">
                <AppBreadcrumb title={t("Paramètres")} />
                {fetching ? <FecthingSpinner /> : (
                    <>
                        <div className="row clearfix settings-grid">
                            {sectionsConfig.map(section => (
                                <div className="col-12" key={section.key}>
                                    <div className="card settings-card">
                                        <div className="header settings-card__header">
                                            <div>
                                                <h2><i className={`fa ${section.icon} mr-2`} aria-hidden="true" />{section.title}</h2>
                                                <small className="text-muted">{section.description}</small>
                                            </div>
                                            {!section.unavailable && (
                                                <button type="button" className="btn btn-warning text-white btn-sm font-weight-bold" onClick={() => onSettingEdit(section.key)}>
                                                    <i className="fa fa-edit"></i>{' '}
                                                    <span>{t("Modifier")}</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="body p-0">
                                            <table className="table table-hover mb-0 settings-table">
                                                <tbody>
                                                    {section.rows.map(row => (
                                                        <tr key={row.label}>
                                                            <td>{row.label}</td>
                                                            <td className="text-right">{row.value}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className={`modal fade ${currentSection ? 'd-block show' : ''}`} style={{ overflowY: 'auto' }} onClick={() => null}>
                            <div className="modal-dialog modal-lg" role="document">
                                {currentSection && (
                                    <Form onSubmit={formik.handleSubmit}>
                                        <div className="modal-content">
                                            <div className="modal-header">
                                                <h5 className="modal-title">{currentSection.title}</h5>
                                                <button type="button" className="close" aria-label="Close" onClick={onCancelSettingEdit}>
                                                    <span aria-hidden="true">×</span>
                                                </button>
                                            </div>
                                            <div className="modal-body">
                                                {alert && <AppAlert onClose={onClose} type={alert.type}>{alert.message}</AppAlert>}
                                                {currentSection.hint && <small className="text-muted d-block mb-3">{currentSection.hint}</small>}
                                                <div className="row">
                                                    {currentSection.fields.map(field => (
                                                        <Col md={field.md || 12} key={field.name}>
                                                            {renderField(field)}
                                                        </Col>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="modal-footer">
                                                <button type="button" className="btn btn-round btn-danger font-weight-bold" disabled={submitting} onClick={onCancelSettingEdit}>
                                                    <span className="fa fa-times"></span>{' '}{t("Annuler")}
                                                </button>
                                                <button type="submit" className="btn btn-round btn-success font-weight-bold" disabled={submitting}>
                                                    <span className="fa fa-save"></span>{' '}{submitting ? t('Enregistrement...') : t('Enregistrer')}
                                                </button>
                                            </div>
                                        </div>
                                    </Form>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default Setting;